"use client";

import { FC, useEffect } from "react";
import { getTrackableHtmlElement, getTrackingData } from "../utils";
import { usePathname, useSearchParams } from "next/navigation";
import { useTrackIntersectionObserver } from "./useTrackIntersectionObserver";
import { SearchEventSchema } from "../e-commerce/events-schemas";
import { useTrackEvent } from "../TrackingContext";

const useAutoTrackClick = () => {
  const trackEvent = useTrackEvent();
  useEffect(() => {
    const listener = ({ target }: MouseEvent) => {
      const trackingElement = getTrackableHtmlElement(target);
      if (trackingElement) {
        const trackingData = getTrackingData(trackingElement);
        trackEvent({ eventName: "click", ...trackingData });
      }
    };
    window.addEventListener("click", listener);
    return () => {
      window.removeEventListener("click", listener);
    };
  }, [trackEvent]);
};

const useTrackPageViews = () => {
  const pathname = usePathname();
  const trackEvent = useTrackEvent();
  useEffect(() => {
    if (pathname) trackEvent({ eventName: "page_view" });
  }, [pathname, trackEvent]);
};

const useSearchTrack = () => {
  const searchParams = useSearchParams();
  const query = searchParams.get("query");
  const trackEvent = useTrackEvent();
  useEffect(() => {
    if (query)
      trackEvent(
        SearchEventSchema.parse({ eventName: "search", search_term: query }),
      );
  }, [query, trackEvent]);
};

export const AutoTracker: FC = () => {
  useAutoTrackClick();
  useTrackPageViews();
  useTrackIntersectionObserver();
  useSearchTrack();
  return null;
};
